import { Fragment, Slice } from '@tiptap/pm/model';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';
import { CellSelection, selectedRect, selectionCell } from '@tiptap/pm/tables';

type TableRect = ReturnType<typeof selectedRect>;
type SplitDirection = 'up' | 'down';

interface TableSplitContext {
  rect: TableRect;
  selectedStartRow: number;
  selectedEndRowExclusive: number;
}

/**
 * Returns whether the current table selection can be split into two tables.
 */
export function canSplitTableAfterSelection(state: EditorState) {
  return getTableSplitContext(state) !== null;
}

/**
 * Returns whether the current table can be split above the focused cell row.
 */
export function canSplitTableUp(state: EditorState) {
  return getDirectionalTableSplitContext(state, 'up') !== null;
}

/**
 * Returns whether the current table can be split below the focused cell row.
 */
export function canSplitTableDown(state: EditorState) {
  return getDirectionalTableSplitContext(state, 'down') !== null;
}

/**
 * Splits the selected table after the selected row block.
 */
export function splitTableAfterSelection(state: EditorState, dispatch?: (tr: Transaction) => void) {
  const context = getTableSplitContext(state);

  if (!context) {
    return false;
  }

  if (dispatch) {
    const tr = state.tr;
    applyTableSplit(tr, context);
    dispatch(tr);
  }

  return true;
}

/**
 * Splits the current table above the focused cell row.
 */
export function splitTableUp(state: EditorState, dispatch?: (tr: Transaction) => void) {
  return splitTableAtFocusedRow(state, 'up', dispatch);
}

/**
 * Splits the current table below the focused cell row.
 */
export function splitTableDown(state: EditorState, dispatch?: (tr: Transaction) => void) {
  return splitTableAtFocusedRow(state, 'down', dispatch);
}

/**
 * Splits the current table at the focused cell row boundary in the requested direction.
 */
function splitTableAtFocusedRow(state: EditorState, direction: SplitDirection, dispatch?: (tr: Transaction) => void) {
  const context = getDirectionalTableSplitContext(state, direction);

  if (!context) {
    return false;
  }

  if (dispatch) {
    const tr = state.tr;
    applyTwoTableSplit(tr, context, direction);
    dispatch(tr);
  }

  return true;
}

/**
 * Returns the derived split context when the current selection is valid.
 */
export function getTableSplitContext(state: EditorState): TableSplitContext | null {
  if (!(state.selection instanceof CellSelection)) {
    return null;
  }

  const context = getSelectedCellContext(state);

  if (!context) {
    return null;
  }

  const { rect, anchorCellRect, headCellRect } = context;
  const selectedStartRow = Math.min(anchorCellRect.top, headCellRect.top);
  const selectedEndRowExclusive = Math.max(anchorCellRect.top, headCellRect.top) + 1;

  if (rect.left !== 0 || rect.right !== rect.map.width) {
    return null;
  }

  if (selectedStartRow === 0 && selectedEndRowExclusive === rect.map.height) {
    return null;
  }

  if (hasBoundarySpan(rect, selectedStartRow) || hasBoundarySpan(rect, selectedEndRowExclusive)) {
    return null;
  }

  return { rect, selectedStartRow, selectedEndRowExclusive };
}

/**
 * Returns the derived split context for splitting above or below the focused cell row.
 */
function getDirectionalTableSplitContext(state: EditorState, direction: SplitDirection): TableSplitContext | null {
  const context = getSelectedCellContext(state);

  if (!context) {
    return null;
  }

  const { rect, focusCellRect } = context;
  const selectedStartRow = direction === 'up' ? focusCellRect.top : 0;
  const selectedEndRowExclusive = direction === 'up' ? rect.map.height : focusCellRect.bottom;
  const splitRow = direction === 'up' ? focusCellRect.top : focusCellRect.bottom;

  if ((direction === 'up' && splitRow <= 0) || (direction === 'down' && splitRow >= rect.map.height)) {
    return null;
  }

  if (hasBoundarySpan(rect, splitRow)) {
    return null;
  }

  return { rect, selectedStartRow, selectedEndRowExclusive };
}

/**
 * Returns the current table rect plus the relevant cell rects for the selection.
 */
function getSelectedCellContext(state: EditorState) {
  try {
    const rect = selectedRect(state);
    const selection = state.selection;

    if (selection instanceof CellSelection) {
      return {
        rect,
        focusCellRect: rect.map.findCell(selection.$anchorCell.pos - rect.tableStart),
        anchorCellRect: rect.map.findCell(selection.$anchorCell.pos - rect.tableStart),
        headCellRect: rect.map.findCell(selection.$headCell.pos - rect.tableStart),
      };
    }

    const $cell = selectionCell(state);
    const focusCellRect = rect.map.findCell($cell.pos - rect.tableStart);

    return {
      rect,
      focusCellRect,
      anchorCellRect: focusCellRect,
      headCellRect: focusCellRect,
    };
  } catch {
    return null;
  }
}

/**
 * Returns whether any rowspan crosses the requested split boundary.
 */
function hasBoundarySpan(rect: TableRect, splitRow: number) {
  if (splitRow <= 0 || splitRow >= rect.map.height) {
    return false;
  }

  const topBoundaryRow = splitRow - 1;
  const bottomBoundaryRow = splitRow;

  for (let col = 0; col < rect.map.width; col++) {
    const topCell = rect.map.map[topBoundaryRow * rect.map.width + col];
    const bottomCell = rect.map.map[bottomBoundaryRow * rect.map.width + col];

    if (topCell === bottomCell) {
      return true;
    }
  }

  return false;
}

/**
 * Applies the split-table replacement transaction and moves selection into the selected table.
 */
function applyTableSplit(tr: Transaction, { rect, selectedStartRow, selectedEndRowExclusive }: TableSplitContext) {
  const tablePos = rect.tableStart - 1;
  const rows = Array.from({ length: rect.table.childCount }, (_, index) => rect.table.child(index));
  const beforeRows = rows.slice(0, selectedStartRow);
  const selectedRows = rows.slice(selectedStartRow, selectedEndRowExclusive);
  const afterRows = rows.slice(selectedEndRowExclusive);
  const tables = [beforeRows, selectedRows, afterRows]
    .filter((tableRows) => tableRows.length > 0)
    .map((tableRows) =>
      rect.table.type.createChecked(rect.table.attrs, Fragment.fromArray(tableRows), rect.table.marks)
    );
  const replacement = new Slice(Fragment.fromArray(tables), 0, 0);

  tr.replace(tablePos, tablePos + rect.table.nodeSize, replacement);

  const selectedTablePos = tablePos + (beforeRows.length > 0 ? tables[0].nodeSize : 0);
  tr.setSelection(TextSelection.near(tr.doc.resolve(selectedTablePos + 1)));
  tr.scrollIntoView();
}

/**
 * Applies a two-table split and keeps selection in the table containing the focused row.
 */
function applyTwoTableSplit(tr: Transaction, context: TableSplitContext, direction: SplitDirection) {
  const tablePos = context.rect.tableStart - 1;
  const rows = Array.from({ length: context.rect.table.childCount }, (_, index) => context.rect.table.child(index));
  const topRows = rows.slice(0, context.selectedEndRowExclusive);
  const bottomRows = rows.slice(context.selectedEndRowExclusive);
  const topBoundaryRows = rows.slice(0, context.selectedStartRow);
  const bottomBoundaryRows = rows.slice(context.selectedStartRow);
  const splitRows = direction === 'up' ? [topBoundaryRows, bottomBoundaryRows] : [topRows, bottomRows];
  const tables = splitRows.map((tableRows) =>
    context.rect.table.type.createChecked(
      context.rect.table.attrs,
      Fragment.fromArray(tableRows),
      context.rect.table.marks
    )
  );
  const replacement = new Slice(Fragment.fromArray(tables), 0, 0);

  tr.replace(tablePos, tablePos + context.rect.table.nodeSize, replacement);

  const selectedTablePos = direction === 'up' ? tablePos + tables[0].nodeSize : tablePos;
  const rowOffset =
    direction === 'up' ? 0 : rows.slice(0, context.selectedStartRow).reduce((sum, row) => sum + row.nodeSize, 0);

  tr.setSelection(TextSelection.near(tr.doc.resolve(selectedTablePos + 1 + rowOffset)));
  tr.scrollIntoView();
}
